import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { registerUser } from '../api/auth';
import { passwordError } from '../utils/password';
import AvatarSelector from './AvatarSelector';
import PasswordField, { PasswordMatchHint } from './PasswordField';

const RegistrationForm = () => {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [avatar, setAvatar] = useState('potato_avatar_1.svg');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const history = useHistory();

    useEffect(() => {
        if (!error) return;
        document.querySelector('.auth-form .sc-alert')?.scrollIntoView({ block: 'nearest' });
    }, [error]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const weakness = passwordError(password);
        if (weakness) {
            setError(weakness);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsSubmitting(true);

        try {
            const data = await registerUser(email, username, password, avatar);
            setSuccess(data.message || 'Registration successful! Please check your email to verify your account.');
        } catch (err) {
            setError(err.message || 'Registration failed. Please try again.');
            setIsSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="auth-success">
                <div className="sc-alert sc-alert--success" role="status">{success}</div>
                <button
                    type="button"
                    className="sc-btn sc-btn--primary sc-btn--block"
                    onClick={() => history.push('/login')}
                >
                    Go to sign in
                </button>
            </div>
        );
    }

    return (
        <form className="auth-form" onSubmit={handleSubmit}>
            {error && <div className="sc-alert sc-alert--error" role="alert">{error}</div>}

            <div className="auth-field">
                <label htmlFor="email">Email address</label>
                <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    disabled={isSubmitting}
                />
            </div>

            <div className="auth-field">
                <label htmlFor="username">Username</label>
                <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Choose a username"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    disabled={isSubmitting}
                    minLength={3}
                />
                <p className="auth-note">At least 3 characters.</p>
            </div>

            <PasswordField
                id="password"
                name="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                autoComplete="new-password"
                disabled={isSubmitting}
                showRules
            />

            <div className="auth-field-stack">
                <PasswordField
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your password"
                    autoComplete="new-password"
                    disabled={isSubmitting}
                />
                <PasswordMatchHint password={password} confirm={confirmPassword} />
            </div>

            <AvatarSelector
                selectedAvatar={avatar}
                onAvatarSelect={setAvatar}
            />

            <button
                type="submit"
                className="sc-btn sc-btn--primary sc-btn--block"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Creating account...' : 'Create account'}
            </button>

            <p className="auth-footer">
                Already have an account? <Link to="/login">Sign in</Link>
            </p>
        </form>
    );
};

export default RegistrationForm;
